import { AuthorDTO } from '@/types/AuthorDTO';
import { DepartmentDTO } from '@/types/DepartmentDTO';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PaymentMethod } from '@/types/payment/PaymentMethod';
import { PaymentProvider } from '@/types/payment/PaymentProvider';
import { RegionDTO } from '@/types/RegionDTO';
import { RootState } from '@/app/store';
import { SenderDTO } from '@/types/SenderDTO';
import { SenderInfoDTO } from '@/types/create/SenderInfoDTO';
import { StagedPhotoDTO } from '@/types/create/StagedPhotoDTO';
import { SudokuDifficulty } from '@/types/create/SudokuDifficulty';
import { SudokuType } from '@/types/create/SudokuType';
import { UserDTO } from '@/types/user/UserDTO';
import { formatUserFullName } from '@/utils/string-utils';
import { v4 as uuid } from 'uuid';

export interface CreateFormState {
  region: RegionDTO | null;
  department: DepartmentDTO | null;
  author: AuthorDTO | null;
  sender: SenderDTO | null;
  letterText?: string;
  isAnswerOrdered: boolean;
  stagedPhotos: StagedPhotoDTO[];
  cardAuthorEmail: string;
  answersCount: number;
  sudokuAttached: boolean;
  sudokuCount: number;
  sudokuType: SudokuType;
  sudokuDifficulty: SudokuDifficulty;
  crosswordAttached: boolean;
  crosswordCount: number;
  newsAttached: boolean;
  sportsNewsAttached: boolean;
  isLetterSent: boolean;
  paymentMethod: PaymentMethod;
  paymentProvider?: PaymentProvider;
  letterNumber?: number;
  letterUuid?: string;
  letterDraftId?: number;
  idempotencyKey?: string;
}

const initialState: CreateFormState = {
  region: null,
  department: null,
  isAnswerOrdered: false,
  author: null,
  sender: null,
  stagedPhotos: [],
  cardAuthorEmail: '',
  answersCount: 1,
  isLetterSent: false,
  paymentMethod: PaymentMethod.SBP,
  paymentProvider: PaymentProvider.ALFA,
  idempotencyKey: uuid(),
  sudokuAttached: false,
  sudokuCount: 1,
  sudokuType: 'CLASSIC',
  sudokuDifficulty: 'MEDIUM',
  crosswordAttached: false,
  crosswordCount: 1,
  newsAttached: false,
  sportsNewsAttached: false,
};

export const createFormSlice = createSlice({
  name: 'createForm',
  initialState,
  reducers: {
    setRegionAndDepartment(
      state: CreateFormState,
      action: PayloadAction<{
        region: RegionDTO | null;
        department: DepartmentDTO | null;
      }>,
    ): void {
      state.region = action.payload.region;
      state.department = action.payload.department;
      if (action.payload.department?.allowPhoto === false) {
        // A department that takes no photos would have the letter refused at creation while the
        // staged photos went on being priced, so choosing one drops them here. Only an explicit
        // refusal counts: a department that says nothing about photos keeps them.
        state.stagedPhotos = [];
      }
    },
    setAuthor(state: CreateFormState, action: PayloadAction<AuthorDTO>): void {
      state.author = action.payload;
    },
    setSender(state: CreateFormState, action: PayloadAction<SenderDTO>): void {
      state.sender = action.payload;
    },
    setLetterTextForm(state: CreateFormState, action: PayloadAction<string>): void {
      state.letterText = action.payload;
    },
    setAnswerOrderedForm(state: CreateFormState, action: PayloadAction<boolean>): void {
      state.isAnswerOrdered = action.payload;
    },
    setAnswersCountForm(state: CreateFormState, action: PayloadAction<number>): void {
      state.answersCount = action.payload;
    },
    setSudokuAttachedState(state: CreateFormState, action: PayloadAction<boolean>): void {
      state.sudokuAttached = action.payload;
    },
    setSudokuCountState(state: CreateFormState, action: PayloadAction<number>): void {
      state.sudokuCount = action.payload;
    },
    setSudokuTypeState(state: CreateFormState, action: PayloadAction<SudokuType>): void {
      state.sudokuType = action.payload;
    },
    setSudokuDifficultyState(state: CreateFormState, action: PayloadAction<SudokuDifficulty>): void {
      state.sudokuDifficulty = action.payload;
    },
    setCrosswordAttachedState(state: CreateFormState, action: PayloadAction<boolean>): void {
      state.crosswordAttached = action.payload;
    },
    setCrosswordCountState(state: CreateFormState, action: PayloadAction<number>): void {
      state.crosswordCount = action.payload;
    },
    setNewsAttachedState(state: CreateFormState, action: PayloadAction<boolean>): void {
      state.newsAttached = action.payload;
    },
    setSportsNewsAttachedState(state: CreateFormState, action: PayloadAction<boolean>): void {
      state.sportsNewsAttached = action.payload;
    },
    setLetterSentForm(state: CreateFormState, action: PayloadAction<boolean>): void {
      state.isLetterSent = action.payload;
    },
    setLetterNumber(state: CreateFormState, action: PayloadAction<number>): void {
      state.letterNumber = action.payload;
    },
    setLetterUuid(state: CreateFormState, action: PayloadAction<string | undefined>): void {
      state.letterUuid = action.payload;
    },
    setPaymentMethodForm(state: CreateFormState, action: PayloadAction<PaymentMethod>): void {
      state.paymentMethod = action.payload;
    },
    setPaymentProviderForm(state: CreateFormState, action: PayloadAction<PaymentProvider | undefined>): void {
      state.paymentProvider = action.payload;
    },
    clearFormState(state: CreateFormState): void {
      state.isAnswerOrdered = false;
      state.letterText = undefined;
      state.stagedPhotos = [];
      state.region = null;
      state.department = null;
      state.sender = null;
      state.author = null;
      state.answersCount = 1;
      state.isLetterSent = false;
      state.letterNumber = undefined;
      state.letterUuid = undefined;
      state.paymentMethod = PaymentMethod.SBP;
      state.paymentProvider = PaymentProvider.ALFA;
      state.letterDraftId = undefined;
      state.idempotencyKey = uuid();
      state.sudokuAttached = false;
      state.sudokuCount = 1;
      state.sudokuType = 'CLASSIC';
      state.sudokuDifficulty = 'MEDIUM';
      state.crosswordAttached = false;
      state.crosswordCount = 1;
      state.newsAttached = false;
      state.sportsNewsAttached = false;
    },
    addStagedPhoto(state: CreateFormState, action: PayloadAction<StagedPhotoDTO>): void {
      // The array may be missing in a session persisted before the field existed.
      state.stagedPhotos = [...(state.stagedPhotos ?? []), action.payload];
    },
    removeStagedPhoto(state: CreateFormState, action: PayloadAction<string>): void {
      state.stagedPhotos = (state.stagedPhotos ?? []).filter(
        (photo: StagedPhotoDTO): boolean => photo.id !== action.payload,
      );
    },
    setStagedPhotos(state: CreateFormState, action: PayloadAction<StagedPhotoDTO[]>): void {
      state.stagedPhotos = action.payload;
    },
    clearStagedPhotos(state: CreateFormState): void {
      state.stagedPhotos = [];
    },
    setCreateInfoFromDTO(state: CreateFormState, action: PayloadAction<SenderInfoDTO>): void {
      state.region = action.payload.region;
      state.department = action.payload.department;
      state.sender = action.payload.sender;
      state.isAnswerOrdered = false;
      state.letterText = undefined;
      state.stagedPhotos = [];
      state.answersCount = 1;
      state.sudokuAttached = false;
      state.sudokuCount = 1;
      state.sudokuType = 'CLASSIC';
      state.sudokuDifficulty = 'MEDIUM';
      state.crosswordAttached = false;
      state.crosswordCount = 1;
      state.newsAttached = false;
      state.sportsNewsAttached = false;
    },
    setAuthorFromUser(state: CreateFormState, action: PayloadAction<UserDTO>): void {
      state.author = {
        name: formatUserFullName(action.payload),
        phone: action.payload.phone || '',
        email: action.payload.email,
      };
    },
    setPhoneNumber(state: CreateFormState, action: PayloadAction<string>): void {
      state.author = {
        email: state.author?.email || '',
        name: state.author?.name || '',
        phone: action.payload,
      };
    },
    clearLetterTextAndFile(state: CreateFormState): void {
      state.letterText = undefined;
      state.stagedPhotos = [];
      state.sudokuAttached = false;
      state.crosswordAttached = false;
      state.newsAttached = false;
      state.sportsNewsAttached = false;
    },
    setCardAuthorEmail(state: CreateFormState, action: PayloadAction<string>): void {
      state.cardAuthorEmail = action.payload;
    },
    setLetterDraftId(state: CreateFormState, action: PayloadAction<number>): void {
      state.letterDraftId = action.payload;
    },
    clearLetterDraftId(state: CreateFormState): void {
      state.letterDraftId = undefined;
    },
    refreshIdempotencyKey(state: CreateFormState): void {
      state.idempotencyKey = uuid();
    },
  },
});

export const {
  setRegionAndDepartment,
  setAuthor,
  setSender,
  setCreateInfoFromDTO,
  setAuthorFromUser,
  setPhoneNumber,
  setAnswerOrderedForm,
  setLetterTextForm,
  clearFormState,
  addStagedPhoto,
  removeStagedPhoto,
  setStagedPhotos,
  clearStagedPhotos,
  clearLetterTextAndFile,
  setCardAuthorEmail,
  setAnswersCountForm,
  setLetterSentForm,
  setLetterNumber,
  setLetterUuid,
  setPaymentMethodForm,
  setPaymentProviderForm,
  setLetterDraftId,
  clearLetterDraftId,
  refreshIdempotencyKey,
  setCrosswordAttachedState,
  setCrosswordCountState,
  setSudokuAttachedState,
  setSudokuCountState,
  setSudokuTypeState,
  setSudokuDifficultyState,
  setNewsAttachedState,
  setSportsNewsAttachedState,
} = createFormSlice.actions;

export const getRegion = (state: RootState): RegionDTO | null => {
  return state?.createForm?.region ?? initialState.region;
};

export const getDepartment = (state: RootState): DepartmentDTO | null => {
  return state?.createForm?.department ?? initialState.department;
};

export const getLetterText = (state: RootState): string | undefined => {
  return state?.createForm?.letterText ?? initialState.letterText;
};

export const getSudokuAttached = (state: RootState): boolean => {
  return state?.createForm?.sudokuAttached ?? initialState.sudokuAttached;
};

export const getSudokuCount = (state: RootState): number => {
  return state?.createForm?.sudokuCount ?? initialState.sudokuCount;
};

export const getSudokuType = (state: RootState): SudokuType => {
  // Fallback for sessions persisted before the field existed (and null-safe during hydration).
  return state?.createForm?.sudokuType ?? initialState.sudokuType;
};

export const getSudokuDifficulty = (state: RootState): SudokuDifficulty => {
  // Fallback for sessions persisted before the field existed (and null-safe during hydration).
  return state?.createForm?.sudokuDifficulty ?? initialState.sudokuDifficulty;
};

export const getCrosswordAttached = (state: RootState): boolean => {
  return state?.createForm?.crosswordAttached ?? initialState.crosswordAttached;
};

export const getCrosswordCount = (state: RootState): number => {
  return state.createForm.crosswordCount;
};

export const getNewsAttached = (state: RootState): boolean => {
  return state?.createForm?.newsAttached ?? initialState.newsAttached;
};

export const getSportsNewsAttached = (state: RootState): boolean => {
  return state.createForm.sportsNewsAttached;
};

export const getAnswerOrder = (state: RootState): boolean => {
  return state?.createForm?.isAnswerOrdered ?? initialState.isAnswerOrdered;
};

export const getAnswersCount = (state: RootState): number => {
  return state?.createForm?.answersCount ?? initialState.answersCount;
};

export const getAuthor = (state: RootState): AuthorDTO | null => {
  return state?.createForm?.author ?? initialState.author;
};

export const getSender = (state: RootState): SenderDTO | null => {
  return state?.createForm?.sender ?? initialState.sender;
};

export const getCardAuthorEmail = (state: RootState): string => {
  return state?.createForm?.cardAuthorEmail ?? initialState.cardAuthorEmail;
};

export const getLetterSent = (state: RootState): boolean => {
  return state?.createForm?.isLetterSent ?? initialState.isLetterSent;
};

export const getLetterNumber = (state: RootState): number | undefined => {
  return state?.createForm?.letterNumber;
};

export const getLetterUuid = (state: RootState): string | undefined => {
  return state?.createForm?.letterUuid;
};

export const getPaymentMethod = (state: RootState): PaymentMethod => {
  return state?.createForm?.paymentMethod ?? initialState.paymentMethod;
};

export const getPaymentProvider = (state: RootState): PaymentProvider | undefined => {
  return state?.createForm?.paymentProvider;
};

export const getLetterDraftId = (state: RootState): number | undefined => {
  return state?.createForm?.letterDraftId;
};

export const getIdempotencyKey = (state: RootState): string | undefined => {
  return state?.createForm?.idempotencyKey;
};

export const getStagedPhotos = (state: RootState): StagedPhotoDTO[] => {
  // Fallback for sessions persisted before the field existed (and null-safe during hydration).
  return state?.createForm?.stagedPhotos ?? initialState.stagedPhotos;
};
